import type { Components } from '@mui/material/styles'
import { lighten } from '@mui/system'

import { california, grassGreen, lightGreen, microGreen, redOrange } from '../colors'
import type { Theme } from '../types'

function getContainedVars(color: string): Record<string, string> {
	const vars = {
		'--Button-containedBg': `var(--mui-palette-${color}-dark)`,
		'--Button-containedHoverBg': `var(--mui-palette-${color}-hovered)`,
		'--Button-containedActiveBg': `var(--mui-palette-${color}-activated)`,
	}

	// Use our light green instead of our set primary color which is the darker grass green
	if (color === 'primary') {
		vars['--Button-containedBg'] = lightGreen[200]
		vars['--Button-containedHoverBg'] = '#adf1c1'
		vars['--Button-containedActiveBg'] = '#adf1c1'
	}

	if (color === 'secondary') {
		vars['--Button-containedBg'] = grassGreen[700]
		vars['--Button-containedHoverBg'] = grassGreen[600]
		vars['--Button-containedActiveBg'] = grassGreen[600]
		vars['color'] = 'var(--mui-palette-common-white)'
	}

	if (color === 'warning') {
		vars['--Button-containedBg'] = california[500]
		vars['--Button-containedHoverBg'] = lighten(california[500], 0.15)
		vars['--Button-containedActiveBg'] = lighten(california[500], 0.15)
		vars['color'] = 'var(--mui-palette-common-white)'
	}

	if (color === 'error') {
		vars['--Button-containedBg'] = redOrange[500]
		vars['--Button-containedHoverBg'] = lighten(redOrange[500], 0.15)
		vars['--Button-containedActiveBg'] = lighten(redOrange[500], 0.15)
		vars['color'] = 'var(--mui-palette-common-white)'
	}

	return vars
}

function getOutlinedVars(color: string, dark: boolean): Record<string, string> {
	const vars = {
		'--Button-outlinedBorder': `var(--mui-palette-${color}-main)`,
		'--Button-outlinedHoverBg': `var(--mui-palette-${color}-hovered)`,
		'--Button-outlinedActiveBg': `var(--mui-palette-${color}-activated)`,
	}

	// Custom case for secondary
	if (color === 'secondary') {
		if (dark) {
			vars['--Button-outlinedBorder'] = 'var(--mui-palette-secondary-700)'
		} else {
			vars['--Button-outlinedBorder'] = 'var(--mui-palette-secondary-200)'
		}
	}

	return vars
}

function getTextVars(color: string): Record<string, string> {
	return {
		'--Button-textHoverBg': `var(--mui-palette-${color}-hovered)`,
		'--Button-textActiveBg': `var(--mui-palette-${color}-activated)`,
	}
}

export const MuiButton = {
	defaultProps: { disableRipple: true },
	styleOverrides: {
		root: {
			borderRadius: '8px',
			minHeight: 'var(--Button-minHeight)',
			minWidth: 'unset',
			textTransform: 'none',
			'&:focus-visible': { outline: '2px solid var(--mui-palette-primary-main)' },
		},
		text: {
			'&:hover': { backgroundColor: 'var(--Button-textHoverBg)' },
			'&:active': { backgroundColor: 'var(--Button-textActiveBg)' },
		},
		textPrimary: getTextVars('primary'),
		textSecondary: getTextVars('secondary'),
		textSuccess: getTextVars('success'),
		textInfo: getTextVars('info'),
		textWarning: getTextVars('warning'),
		textError: getTextVars('error'),
		outlined: {
			boxShadow: 'var(--mui-shadows-1)',
			borderColor: 'var(--Button-outlinedBorder)',
			'&:hover': {
				borderColor: 'var(--Button-outlinedBorder)',
				backgroundColor: 'var(--Button-outlinedHoverBg)',
			},
			'&:active': { backgroundColor: 'var(--Button-outlinedActiveBg)' },
		},
		outlinedPrimary: ({ theme }) => {
			return getOutlinedVars('primary', theme.palette.mode === 'dark')
		},
		outlinedSecondary: ({ theme }) => {
			return getOutlinedVars('secondary', theme.palette.mode === 'dark')
		},
		outlinedSuccess: ({ theme }) => {
			return getOutlinedVars('success', theme.palette.mode === 'dark')
		},
		outlinedInfo: ({ theme }) => {
			return getOutlinedVars('info', theme.palette.mode === 'dark')
		},
		outlinedWarning: ({ theme }) => {
			return getOutlinedVars('warning', theme.palette.mode === 'dark')
		},
		outlinedError: ({ theme }) => {
			return getOutlinedVars('error', theme.palette.mode === 'dark')
		},
		contained: {
			backgroundColor: 'var(--Button-containedBg)',
			color: microGreen[900],
			fontWeight: 600,
			overflow: 'hidden',
			'&:hover': { backgroundColor: 'var(--Button-containedHoverBg)' },
			'&:active': { backgroundColor: 'var(--Button-containedActiveBg)' },
			'&.Mui-disabled': {
				backgroundColor: 'var(--mui-palette-action-disabledBackground)',
				color: 'var(--mui-palette-action-disabled)',
			},
		},
		containedPrimary: getContainedVars('primary'),
		containedSecondary: getContainedVars('secondary'),
		containedSuccess: getContainedVars('success'),
		containedInfo: getContainedVars('info'),
		containedWarning: getContainedVars('warning'),
		containedError: getContainedVars('error'),
		sizeSmall: { '--Button-minHeight': '32x', fontSize: '0.8125rem', lineHeight: '24px' },
		sizeMedium: { '--Button-minHeight': '40x', fontSize: '0.875rem', lineHeight: '28px' },
		sizeLarge: { '--Button-minHeight': '48x', fontSize: '0.9375rem', lineHeight: '32px' },
	},
} satisfies Components<Theme>['MuiButton']
